/**
 * Competitive Landscape (slide 8) — feature-comparison matrix.
 * Mirrors backend/pitch_competitive.py exactly.
 *
 * Canonical format:
 *
 *   Competitors
 *   - Capital Connect
 *   - OpenVC
 *
 *   Features
 *   - Automated investor documents | yes,no
 *
 *   Takeaway
 *   One-sentence why-we-win.
 */

export const MAX_FEATURES = 8;
export const MAX_COMPETITORS = 3;

const TRUE_MARKS = new Set(["yes", "y", "true", "1", "✓", "tick"]);

function stripBullet(line) {
  return line.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim();
}

export function parseCompetitive(content) {
  const out = { competitors: [], features: [], takeaway: "" };
  if (!content) return out;
  const blocks = content.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  for (const block of blocks) {
    const lines = block.split(/\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) continue;
    const header = lines[0].toLowerCase();
    if (header === "competitors") {
      out.competitors = lines.slice(1).map(stripBullet).filter(Boolean).slice(0, MAX_COMPETITORS);
    } else if (header === "features") {
      const feats = [];
      for (const ln of lines.slice(1)) {
        const s = stripBullet(ln);
        if (!s) continue;
        let text = s;
        let marks = [];
        const idx = s.lastIndexOf("|");
        if (idx > -1) {
          text = s.slice(0, idx).trim();
          marks = s.slice(idx + 1).split(",")
            .map((m) => m.trim().toLowerCase())
            .filter((m) => m !== "")
            .map((m) => TRUE_MARKS.has(m));
        }
        if (text) feats.push({ text, marks: marks.slice(0, MAX_COMPETITORS) });
        if (feats.length >= MAX_FEATURES) break;
      }
      out.features = feats;
    } else if (header === "takeaway") {
      out.takeaway = lines.slice(1).join(" ").trim();
    }
  }
  return out;
}

export function hasMatrix(content) {
  const p = parseCompetitive(content || "");
  return p.features.length > 0 && p.competitors.length > 0;
}

export function assembleCompetitive({ competitors, features, takeaway }) {
  const comps = (competitors || []).map((c) => (c || "").trim()).filter(Boolean).slice(0, MAX_COMPETITORS);
  const blocks = [];
  if (comps.length > 0) blocks.push(`Competitors\n${comps.map((c) => `- ${c}`).join("\n")}`);
  const lines = [];
  for (const f of (features || [])) {
    const text = (f.text || "").trim();
    if (!text) continue;
    const marks = [...(f.marks || [])];
    while (marks.length < comps.length) marks.push(false);
    if (comps.length > 0) {
      lines.push(`- ${text} | ${marks.slice(0, comps.length).map((m) => (m ? "yes" : "no")).join(",")}`);
    } else {
      lines.push(`- ${text}`);
    }
    if (lines.length >= MAX_FEATURES) break;
  }
  if (lines.length > 0) blocks.push(`Features\n${lines.join("\n")}`);
  const tk = (takeaway || "").trim();
  if (tk) blocks.push(`Takeaway\n${tk}`);
  return blocks.join("\n\n");
}
